import { useIntl } from 'react-intl';
import NameDialog from './NameDialog';

/** DICOM LO limit — mirrored by the connector's rename validators. */
const MAX_LABEL_LENGTH = 64;

/** Message id saying why a trimmed label breaks the DICOM LO rules, or undefined. */
function labelError(trimmed: string): string | undefined {
    if (trimmed === '') {
        return 'renameDataset.errorEmpty';
    }
    if (trimmed.length > MAX_LABEL_LENGTH) {
        return 'renameDataset.errorTooLong';
    }
    // Backslash is the DICOM multi-value delimiter; control
    // characters are invalid in an LO value.
    // eslint-disable-next-line no-control-regex
    if (/[\\\u0000-\u001f\u007f]/.test(trimmed)) {
        return 'renameDataset.errorInvalidChars';
    }
    return undefined;
}

type Props = {
    open: boolean;
    /** Dialog title, already translated. */
    title: string;
    /** The current name, shown as the editable starting value. */
    initialValue: string;
    onCancel: () => void;
    /** Called with the trimmed new name; only ever a valid one. */
    onConfirm: (value: string) => void;
};

/**
 * Renames a dataset's display label (DICOM LO rules: non-empty, at most
 * 64 characters, no backslash or control characters). Invalid input
 * blocks the confirm — it is never silently corrected (only trimmed).
 */
export default function RenameDatasetDialog({
    open,
    title,
    initialValue,
    onCancel,
    onConfirm,
}: Props) {
    const intl = useIntl();
    return (
        <NameDialog
            open={open}
            title={title}
            label={intl.formatMessage({ id: 'renameDataset.label' })}
            confirmText={intl.formatMessage({ id: 'rename' })}
            initialValue={initialValue}
            validate={labelError}
            onCancel={onCancel}
            onConfirm={onConfirm}
            inputTestId="rename-dataset-input"
            confirmTestId="rename-dataset-confirm"
        />
    );
}
